import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const GREEN = '#0D3B2E';

// ── WRITE LOCK ─────────────────────────────────────────────────
// Phase 1 is read-only: CPR Live reads FROM QuickBooks and never writes to it.
// The Job sync buttons below are the only code that creates Customers/Invoices
// in QuickBooks. They are disabled until writing is deliberately turned on.
// To re-enable later: set WRITES_ENABLED = true.
const WRITES_ENABLED = false;
const fmt = (v) => (v == null || v === '' ? '—' : '$' + Number(v).toLocaleString('en-US'));

export default function QuickBooks() {
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});   // jobId -> true
  const [bulkRunning, setBulkRunning] = useState(false);
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState('sync');       // sync | customers | invoices | payments | summary
  const [rd, setRd] = useState({});             // tab -> data
  const [rdLoading, setRdLoading] = useState(false);
  const [rdErr, setRdErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j] = await Promise.all([api.get('/qbo/status'), api.get('/qbo/jobs')]);
      setStatus(s.data);
      setJobs(j.data.jobs || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load QuickBooks status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) toast.success('QuickBooks connected!');
    if (params.get('error')) toast.error('QuickBooks: ' + params.get('error'));
    if (params.get('connected') || params.get('error')) {
      window.history.replaceState({}, '', '/quickbooks');
    }
  }, [load]);

  async function connect() {
    try {
      const r = await api.get('/qbo/connect');
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not start QuickBooks sign-in');
    }
  }

  async function disconnect() {
    if (!window.confirm('Disconnect QuickBooks? Synced records stay in QuickBooks; only the connection is removed.')) return;
    try { await api.post('/qbo/disconnect'); toast.success('Disconnected'); load(); }
    catch { toast.error('Failed to disconnect'); }
  }

  async function syncOne(job) {
    setSyncing(s => ({ ...s, [job.id]: true }));
    try {
      const r = await api.post(`/qbo/sync/${job.id}`);
      const d = r.data;
      toast.success(`${job.po_number}: invoice ${d.invoice_existed ? 'already in QBO' : 'created'}, ${d.payments_synced} payment${d.payments_synced === 1 ? '' : 's'} synced`);
    } catch (e) {
      toast.error(`${job.po_number}: ${e.response?.data?.error || 'sync failed'}`);
    } finally {
      setSyncing(s => ({ ...s, [job.id]: false }));
      load();
    }
  }

  async function syncAllUnsynced() {
    const targets = jobs.filter(j => !j.qbo_invoice_id && (j.contract_amt || 0) > 0);
    if (!targets.length) { toast('Nothing to sync — every job with a contract amount is already in QuickBooks.'); return; }
    if (!window.confirm(`Sync ${targets.length} job${targets.length === 1 ? '' : 's'} to QuickBooks?`)) return;
    setBulkRunning(true);
    let ok = 0, fail = 0;
    for (const j of targets) {
      try { await api.post(`/qbo/sync/${j.id}`); ok++; }
      catch { fail++; }
    }
    setBulkRunning(false);
    toast[fail ? 'error' : 'success'](`Done: ${ok} synced${fail ? `, ${fail} failed (see table)` : ''}`);
    load();
  }

  const shown = jobs.filter(j => {
    if (!filter.trim()) return true;
    const s = filter.toLowerCase();
    return String(j.po_number || '').toLowerCase().includes(s) || String(j.customer_name || '').toLowerCase().includes(s);
  });

  // Read-only pulls FROM QuickBooks. Loaded on demand, cached per tab.
  const loadRead = useCallback(async (which, force) => {
    if (which === 'sync') return;
    if (rd[which] && !force) return;
    setRdLoading(true); setRdErr('');
    try {
      const r = await api.get(`/qbo/read/${which}`);
      setRd(s2 => ({ ...s2, [which]: r.data }));
    } catch (e) {
      setRdErr(e.response?.data?.error || 'Could not read from QuickBooks');
    } finally { setRdLoading(false); }
  }, [rd]);

  useEffect(() => { if (status?.connected) loadRead(tab); }, [tab, status, loadRead]);

  const connected = status?.connected;
  const TABS = [
    ['sync', 'Job sync'], ['customers', 'Customers'], ['invoices', 'Invoices'],
    ['payments', 'Payments'], ['summary', 'Summary'],
  ];
  const Pill = ({ ok, children }) => (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
      background: ok ? '#e6f4ea' : '#fdecea', color: ok ? '#1d7a3a' : '#b3362a' }}>{children}</span>
  );
  const Stat = ({ label, value, color }) => (
    <div style={{ flex: 1, minWidth: 130, background: '#f7f9f8', border: '1px solid #e6ebe8', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, color: color || GREEN }}>{value}</div>
    </div>
  );

  return (
    <>
      <div className="page-header"><h1>QuickBooks</h1></div>
      <div style={{ maxWidth: 1000 }}>

        {/* connection card */}
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ width: 46, height: 46, borderRadius: 10, background: connected ? '#2CA01C' : '#eee', color: connected ? '#fff' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 20 }}>qb</div>
            <div style={{ flex: 1, minWidth: 220 }}>
              {loading && !status ? 'Loading…' : !status?.configured ? (
                <>
                  <div style={{ fontWeight: 700 }}>Not set up yet</div>
                  <div style={{ fontSize: 12.5, color: '#777' }}>The server needs the QuickBooks app credentials (qbo.config.json) before you can connect.</div>
                </>
              ) : connected ? (
                <>
                  <div style={{ fontWeight: 700 }}>Connected{status.company ? ` — ${status.company}` : ''}</div>
                  <div style={{ fontSize: 12.5, color: '#777' }}>
                    {status.environment === 'sandbox' ? 'Sandbox company · ' : ''}
                    Since {String(status.connected_at || '').slice(0, 10)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 700 }}>Not connected</div>
                  <div style={{ fontSize: 12.5, color: '#777' }}>Sign in with the company's QuickBooks account to enable syncing.</div>
                </>
              )}
            </div>
            {status?.configured && !connected && (
              <button className="btn btn-primary" onClick={connect} style={{ background: '#2CA01C', border: 'none' }}>Connect QuickBooks</button>
            )}
            {connected && (
              <button className="btn" onClick={disconnect} style={{ border: '1px solid #ddd' }}>Disconnect</button>
            )}
          </div>
        </div>

        {/* tab bar */}
        {connected && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {TABS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setTab(k)}
                style={{ border: '1px solid ' + (tab === k ? GREEN : '#ddd'), background: tab === k ? GREEN : '#fff',
                  color: tab === k ? '#fff' : '#444', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {label}
              </button>
            ))}
            {tab !== 'sync' && (
              <button type="button" onClick={() => loadRead(tab, true)} disabled={rdLoading}
                style={{ marginLeft: 'auto', border: '1px solid #ddd', background: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, cursor: 'pointer' }}>
                {rdLoading ? 'Refreshing…' : '↻ Refresh'}
              </button>
            )}
          </div>
        )}

        {connected && tab !== 'sync' && (
          <div style={{ fontSize: 12.5, color: '#777', marginBottom: 12 }}>
            Read-only view of QuickBooks. Nothing here changes your books.
          </div>
        )}
        {connected && tab !== 'sync' && rdErr && (
          <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#b3362a' }}>{rdErr}</div></div>
        )}
        {connected && tab !== 'sync' && rdLoading && !rd[tab] && (
          <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#777' }}>Reading from QuickBooks…</div></div>
        )}

        {/* ── Customers ── */}
        {connected && tab === 'customers' && rd.customers && (
          <div className="card">
            <div className="card-header" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, flex: 1 }}>QuickBooks customers</h3>
              <span style={{ fontSize: 12.5, color: '#777' }}>{rd.customers.in_crm} of {rd.customers.count} matched to CRM jobs</span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                <table>
                  <thead><tr><th>Name</th><th>Contact</th><th>Address</th><th>Balance</th><th>In CRM</th></tr></thead>
                  <tbody>
                    {rd.customers.customers.map(c => (
                      <tr key={c.qbo_id}>
                        <td style={{ fontWeight: 600 }}>
                          {c.display_name}
                          {c.is_project && (
                            <div style={{ fontSize: 11, color: '#777', fontWeight: 400, marginTop: 2 }}>
                              Project{c.parent ? ` under ${c.parent}` : ''}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 12.5 }}>{[c.email, c.phone].filter(Boolean).join(' · ') || '—'}</td>
                        <td style={{ fontSize: 12.5 }}>{c.address || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{c.balance ? fmt(c.balance) : '—'}</td>
                        <td>
                          {c.in_crm ? <Pill ok>✓ {c.crm_po}</Pill> : <Pill>not in CRM</Pill>}
                          {c.matched_on === 'name~' && <div style={{ fontSize: 10.5, color: '#a06a1b', marginTop: 2 }}>by name — verify</div>}
                        </td>
                      </tr>
                    ))}
                    {!rd.customers.count && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999', padding: 24 }}>No customers in QuickBooks.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── Invoices ── */}
        {connected && tab === 'invoices' && rd.invoices && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <Stat label="Billed" value={fmt(rd.invoices.totals.total)} />
              <Stat label="Paid on invoices" value={fmt(rd.invoices.totals.paid)} color="#1d7a3a" />
              <Stat label="Outstanding" value={fmt(rd.invoices.totals.balance)} color="#b3362a" />
              <Stat label="No CRM job" value={rd.invoices.unmatched} />
            </div>
            <div className="card">
              <div className="card-header"><h3 style={{ margin: 0 }}>Invoices — last 24 months</h3></div>
              <div className="card-body" style={{ padding: 0 }}>
                <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                  <table>
                    <thead><tr><th>Invoice #</th><th>Customer</th><th>Date</th><th>Total</th><th>Balance</th><th>Status</th><th>CRM</th></tr></thead>
                    <tbody>
                      {rd.invoices.invoices.map(i => (
                        <tr key={i.qbo_id}>
                          <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{i.doc_number || '—'}</td>
                          <td>{i.customer}</td>
                          <td style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>{i.txn_date}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmt(i.total)}</td>
                          <td style={{ whiteSpace: 'nowrap', color: i.balance > 0 ? '#b3362a' : '#1d7a3a' }}>{fmt(i.balance)}</td>
                          <td style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>
                            {i.status}{i.overdue ? <span style={{ color: '#b3362a', fontWeight: 700 }}> · overdue</span> : ''}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {i.in_crm ? <Pill ok>✓ {i.crm_po}</Pill> : <Pill>none</Pill>}
                            {i.matched_on === 'name~' && <div style={{ fontSize: 10.5, color: '#a06a1b', marginTop: 2 }}>by name — verify</div>}
                            {i.amount_mismatch && (
                              <div style={{ color: '#b3362a', marginTop: 3 }} title="QuickBooks total differs from the CRM contract amount">
                                ⚠ CRM: {fmt(i.crm_contract_amt)}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!rd.invoices.count && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999', padding: 24 }}>No invoices found.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Payments ── */}
        {connected && tab === 'payments' && rd.payments && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <Stat label="Received" value={fmt(rd.payments.total)} color="#1d7a3a" />
              <Stat label="Payments" value={rd.payments.count} />
              <Stat label="No CRM job" value={rd.payments.unmatched} />
            </div>
            <div className="card">
              <div className="card-header"><h3 style={{ margin: 0 }}>Payments received — last 24 months</h3></div>
              <div className="card-body" style={{ padding: 0 }}>
                <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                  <table>
                    <thead><tr><th>Date</th><th>Customer</th><th>Amount</th><th>Method</th><th>Applied to</th><th>CRM</th></tr></thead>
                    <tbody>
                      {rd.payments.payments.map(p => (
                        <tr key={p.qbo_id}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>{p.txn_date}</td>
                          <td>{p.customer}</td>
                          <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmt(p.amount)}</td>
                          <td style={{ fontSize: 12.5 }}>{[p.method, p.ref_num && '#' + p.ref_num].filter(Boolean).join(' ') || '—'}</td>
                          <td style={{ fontSize: 12.5 }}>
                            {p.applied_to.length ? p.applied_to.join(', ') : <span style={{ color: '#b3362a' }}>unapplied</span>}
                          </td>
                          <td>{p.in_crm ? <Pill ok>✓ {p.crm_po}</Pill> : <Pill>none</Pill>}</td>
                        </tr>
                      ))}
                      {!rd.payments.count && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: 24 }}>No payments found.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Summary ── */}
        {connected && tab === 'summary' && rd.summary && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <Stat label="Billed (12 mo)" value={fmt(rd.summary.totals.billed)} />
              <Stat label="Collected (12 mo)" value={fmt(rd.summary.totals.collected)} color="#1d7a3a" />
              <Stat label="Outstanding" value={fmt(rd.summary.totals.outstanding)} color="#b3362a" />
            </div>
            <div className="card">
              <div className="card-header"><h3 style={{ margin: 0 }}>Month by month</h3></div>
              <div className="card-body">
                {(() => {
                  const max = Math.max(1, ...rd.summary.series.map(b => Math.max(b.billed, b.collected)));
                  return rd.summary.series.map(b => (
                    <div key={b.month} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', fontSize: 12.5, marginBottom: 3 }}>
                        <span style={{ fontWeight: 700, width: 76 }}>{b.month}</span>
                        <span style={{ color: '#777' }}>billed {fmt(b.billed)} · collected {fmt(b.collected)}</span>
                      </div>
                      <div style={{ height: 9, background: '#eef1ef', borderRadius: 5, overflow: 'hidden', marginBottom: 3 }}>
                        <div style={{ width: (b.billed / max * 100) + '%', height: '100%', background: GREEN }} />
                      </div>
                      <div style={{ height: 9, background: '#eef1ef', borderRadius: 5, overflow: 'hidden' }}>
                        <div style={{ width: (b.collected / max * 100) + '%', height: '100%', background: '#2CA01C' }} />
                      </div>
                    </div>
                  ));
                })()}
                {!rd.summary.series.length && <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>No activity in this period.</div>}
                <div style={{ fontSize: 11.5, color: '#888', marginTop: 8 }}>
                  <span style={{ display: 'inline-block', width: 9, height: 9, background: GREEN, borderRadius: 2, marginRight: 5 }} />billed
                  <span style={{ display: 'inline-block', width: 9, height: 9, background: '#2CA01C', borderRadius: 2, margin: '0 5px 0 14px' }} />collected
                </div>
              </div>
            </div>
          </>
        )}

        {connected && tab === 'sync' && !WRITES_ENABLED && (
          <div style={{ background: '#fff8e6', border: '1px solid #f0d99a', borderRadius: 10,
            padding: '11px 15px', marginBottom: 14, fontSize: 13, color: '#6b4e10', lineHeight: 1.5 }}>
            <b>Writing to QuickBooks is turned off.</b> These buttons would create customers and invoices in
            QuickBooks, so they are disabled while the integration is read-only. Nothing on this page can
            change CPR&apos;s books.
          </div>
        )}

        {/* jobs sync table */}
        {connected && tab === 'sync' && (
          <div className="card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, flex: 1 }}>Job sync</h3>
              <input placeholder="Filter by PO or name…" value={filter} onChange={(e) => setFilter(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 7, fontSize: 13 }} />
              <button className="btn btn-primary" disabled={!WRITES_ENABLED || bulkRunning}
                onClick={WRITES_ENABLED ? syncAllUnsynced : undefined}
                title={WRITES_ENABLED ? '' : 'Writing to QuickBooks is turned off'}
                style={{ background: WRITES_ENABLED ? GREEN : '#c9cfcc', cursor: WRITES_ENABLED ? 'pointer' : 'not-allowed' }}>
                {bulkRunning ? 'Syncing…' : 'Sync all unsynced'}
              </button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                <table>
                  <thead>
                    <tr><th>PO#</th><th>Customer</th><th>Contract</th><th>QuickBooks</th><th>Payments</th><th></th></tr>
                  </thead>
                  <tbody>
                    {shown.map(j => (
                      <tr key={j.id}>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{j.po_number}</td>
                        <td>{j.customer_name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmt((j.contract_amt || 0) + (j.change_order_amt || 0))}</td>
                        <td>
                          {j.qbo_invoice_id
                            ? <span style={{ color: '#1d7a3a', fontWeight: 600 }}>✓ Invoice synced {j.synced_at ? String(j.synced_at).slice(0, 10) : ''}</span>
                            : j.last_error
                              ? <span style={{ color: '#b3362a', fontSize: 12 }} title={j.last_error}>⚠ {String(j.last_error).slice(0, 60)}</span>
                              : <span style={{ color: '#999' }}>Not synced</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>
                          {j.payments_total > 0 ? `${j.payments_synced}/${j.payments_total} synced` : '—'}
                        </td>
                        <td>
                          <button className="btn btn-sm btn-primary"
                            disabled={!WRITES_ENABLED || !!syncing[j.id] || bulkRunning}
                            onClick={WRITES_ENABLED ? () => syncOne(j) : undefined}
                            title={WRITES_ENABLED ? '' : 'Writing to QuickBooks is turned off'}
                            style={{ background: WRITES_ENABLED ? GREEN : '#c9cfcc', cursor: WRITES_ENABLED ? 'pointer' : 'not-allowed' }}>
                            {syncing[j.id] ? 'Syncing…' : j.qbo_invoice_id ? 'Re-sync payments' : 'Sync'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!shown.length && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: 24 }}>No jobs found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
